import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import { Language, DEFAULT_LANGUAGE, AVAILABLE_LANGUAGES } from './constants'
import { getLanguageFromStorage, setLanguageToStorage } from './storage'
import { isLanguage, parseLanguage } from './utils'
import { changeLanguage } from './action'

// Check default language is available in available languages
if (!AVAILABLE_LANGUAGES.includes(DEFAULT_LANGUAGE)) {
  throw new Error(
    `Default language ${DEFAULT_LANGUAGE} is not available in available languages`,
  )
}

/**
 * Get available languages from provided language array
 * @param languageArray - Array of languages to filter
 * @returns Array of supported languages
 */
export const getAvailableLanguages = (
  languageArray?: (Language | string)[],
): Language[] => {
  // Set default language if no languages provided
  if (!languageArray || !Array.isArray(languageArray)) {
    return [DEFAULT_LANGUAGE]
  }

  // Filter only languages that are supported in our system
  const supportedLanguages = languageArray.filter(isLanguage)

  // If no supported languages found, fallback to default language
  return supportedLanguages.length ? supportedLanguages : [DEFAULT_LANGUAGE]
}

/**
 * Initialize language based on priority:
 * 1. Stored language (if available and supported)
 * 2. Default language from params (if provided and supported)
 * 3. System default language
 *
 * @param defaultLanguage - Optional default language override
 * @param availableLanguages - Optional list of available languages
 * @returns Object with current language and available languages
 */
export const initializeLanguage = async (
  defaultLanguage?: string,
  availableLanguages?: string[],
): Promise<{ currentLanguage: Language; availableLanguages: Language[] }> => {
  const availableLangs = getAvailableLanguages(availableLanguages)
  let lang = DEFAULT_LANGUAGE

  const isBrowser = typeof window !== 'undefined'
  const storedLang = isBrowser ? getLanguageFromStorage() : null
  const isDefaultLangValid =
    defaultLanguage &&
    isLanguage(defaultLanguage) &&
    availableLangs.includes(defaultLanguage)

  // Determine language based on priority
  if (isBrowser) {
    if (storedLang && availableLangs.includes(storedLang)) {
      lang = storedLang
    } else if (isDefaultLangValid) {
      lang = defaultLanguage as Language
      setLanguageToStorage(lang)
    } else {
      setLanguageToStorage(lang)
    }
  } else if (isDefaultLangValid) {
    setLanguageToStorage(lang)
  }

  await changeLanguage(lang)

  return {
    currentLanguage: lang,
    availableLanguages: availableLangs,
  }
}

/**
 * Get initial language from localStorage if available
 * @returns The language to use initially
 */
const getInitialLanguage = (): Language => {
  // Use stored language if in browser and valid
  if (typeof window !== 'undefined') {
    const storedLang = getLanguageFromStorage()
    if (storedLang && AVAILABLE_LANGUAGES.includes(storedLang)) {
      return storedLang
    }
  }
  return DEFAULT_LANGUAGE
}

// --- i18n initialization with optimized loading ---
i18n.use(initReactI18next).init({
  lng: getInitialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  interpolation: {
    escapeValue: false,
  },
  partialBundledLanguages: true,
  resources: {}, // Start with empty resources, load dynamically
})

// Load initial language resources
const initialLanguage = getInitialLanguage()
void changeLanguage(initialLanguage)

// Save language to localStorage
i18n.on('languageChanged', function (lng) {
  const language = parseLanguage(lng)
  setLanguageToStorage(language)
})

export default i18n
